/**
 * Player Hooks
 * React Query hooks for Spotify playback control
 */

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { playerService } from '../services/player';
import type { SelectDeviceRequest, StartPlaybackRequest, SeekRequest, SetVolumeRequest } from '../types';
import { usePlayerWebSocket } from './usePlayerWebSocket';
import { useAuthStore } from '@/stores/auth';
import { usePlayerUIStore } from '@/stores/player';
import { getWsUrl } from '../client';

// Optional player context for auto-activation (avoids circular dependency)
let playerActivateFn: (() => void) | null = null;
export function setPlayerActivateFn(fn: (() => void) | null) {
  playerActivateFn = fn;
}

// Query key factory
export const playerKeys = {
  all: ['player'] as const,
  devices: () => [...playerKeys.all, 'devices'] as const,
  state: () => [...playerKeys.all, 'state'] as const,
};

/**
 * Hook to get available Spotify Connect devices
 */
export function useDevices(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: playerKeys.devices(),
    queryFn: () => playerService.getDevices(),
    staleTime: 30 * 1000, // 30 seconds
    enabled: options?.enabled ?? true,
  });
}

// Global WebSocket instance manager
// This ensures only ONE WebSocket connection exists regardless of how many components use usePlaybackState
let globalWebSocketEnabled = false;
let webSocketEnableCallback: ((enabled: boolean) => void) | null = null;

export function setGlobalWebSocketEnabled(enabled: boolean) {
  globalWebSocketEnabled = enabled;
  if (webSocketEnableCallback) {
    webSocketEnableCallback(enabled);
  }
}

/**
 * Hook to manage the global WebSocket connection
 * ONLY call this from ONE place (PlayerBar)
 */
export function usePlayerWebSocketManager(enabled: boolean) {
  const [localEnabled, setLocalEnabled] = React.useState(enabled);

  React.useEffect(() => {
    webSocketEnableCallback = setLocalEnabled;
    setLocalEnabled(enabled);
    return () => {
      webSocketEnableCallback = null;
    };
  }, [enabled]);

  const { connectionState } = usePlayerWebSocket({
    enabled: localEnabled,
    baseUrl: getWsUrl(),
    getAccessToken: () => {
      return useAuthStore.getState().accessToken;
    },
    onError: (error) => {
      console.error('Player WebSocket error:', error);
    },
  });

  // Update global state when WebSocket connection state changes
  // Disable polling when WebSocket is connected OR actively connecting
  // This prevents unnecessary REST polling during the connection phase
  React.useEffect(() => {
    globalWebSocketEnabled = connectionState === 'connected' || connectionState === 'connecting';
  }, [connectionState]);

  return { connectionState };
}

/**
 * Hook to get current playback state
 * Uses WebSocket for real-time updates with fallback to REST polling
 * Smart polling: polls faster when playing, slower when paused
 * Can be disabled when player is inactive to avoid unnecessary API calls
 */
export function usePlaybackState(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;

  // Note: WebSocket is managed globally by PlayerBar via usePlayerWebSocketManager
  // This hook only reads from the shared React Query cache
  // We don't create a WebSocket connection here to avoid multiple connections

  // Only poll via REST if player is active AND WebSocket is not connected/connecting
  // globalWebSocketEnabled is true when WebSocket is 'connected' or 'connecting'
  // This prevents unnecessary polling during the connection phase
  const shouldPoll = enabled && !globalWebSocketEnabled;

  return useQuery({
    queryKey: playerKeys.state(),
    queryFn: async () => {
      try {
        return await playerService.getPlaybackState();
      } catch (error: any) {
        // If backend returns 404 (NO_ACTIVE_DEVICE), return null instead of throwing
        // This allows UI to handle "no playback" state gracefully
        if (error?.status === 404 && error?.code === 'NO_ACTIVE_DEVICE') {
          return null;
        }
        // Re-throw other errors (auth, network, etc.)
        throw error;
      }
    },
    enabled: shouldPoll, // Only poll when player active AND WebSocket not connected
    refetchInterval: shouldPoll
      ? (query) => {
          const data = query.state.data;
          // Poll every 1 second when playing (for responsive UI)
          // Poll every 5 seconds when paused (to detect changes)
          return data?.isPlaying ? 1000 : 5000;
        }
      : false, // Disable polling when WebSocket is connected or player inactive
    staleTime: 0, // Always consider stale so polling works
    refetchOnMount: enabled, // Only refetch on mount if player is active
    refetchOnWindowFocus: enabled, // Only refetch on focus if player is active
  });
}

/**
 * Hook to select/transfer to a device
 */
export function useSelectDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SelectDeviceRequest) => playerService.selectDevice(request),
    onSuccess: async () => {
      // Immediate refetch (Spotify API might have delay)
      await Promise.all([
        queryClient.refetchQueries({ queryKey: playerKeys.devices() }),
        queryClient.refetchQueries({ queryKey: playerKeys.state() }),
      ]);

      // Refetch again after 500ms and 1500ms to catch Spotify API updates
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: playerKeys.devices() });
        queryClient.refetchQueries({ queryKey: playerKeys.state() });
      }, 500);

      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: playerKeys.devices() });
        queryClient.refetchQueries({ queryKey: playerKeys.state() });
      }, 1500);
    },
  });
}

/**
 * Hook to control playback (play/pause)
 */
export function usePlayerControls() {
  const queryClient = useQueryClient();
  const setShowDeviceSelector = usePlayerUIStore((s) => s.setShowDeviceSelector);

  const play = useMutation({
    mutationFn: (request?: StartPlaybackRequest) => {
      // Auto-activate player when play is called from anywhere in the app
      if (playerActivateFn) {
        playerActivateFn();
      }
      return playerService.play(request);
    },
    onSuccess: async () => {
      // Immediate refetch (Spotify API might have slight delay)
      await queryClient.refetchQueries({ queryKey: playerKeys.state() });

      // Refetch again after 500ms to catch Spotify API updates
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: playerKeys.state() });
      }, 500);
    },
    onError: (error: any) => {
      // If no active device, open device selector modal
      if (error?.status === 404 && error?.code === 'NO_ACTIVE_DEVICE') {
        setShowDeviceSelector(true);
      }
    },
  });

  const pause = useMutation({
    mutationFn: () => playerService.pause(),
    onSuccess: async () => {
      // Immediate refetch
      await queryClient.refetchQueries({ queryKey: playerKeys.state() });

      // Refetch again after 500ms to catch Spotify API updates
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: playerKeys.state() });
      }, 500);
    },
  });

  const next = useMutation({
    mutationFn: () => playerService.next(),
    onSuccess: async () => {
      // Immediate refetch
      await queryClient.refetchQueries({ queryKey: playerKeys.state() });

      // Refetch again after 500ms to catch Spotify API updates
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: playerKeys.state() });
      }, 500);
    },
  });

  const previous = useMutation({
    mutationFn: () => playerService.previous(),
    onSuccess: async () => {
      // Immediate refetch
      await queryClient.refetchQueries({ queryKey: playerKeys.state() });

      // Refetch again after 500ms to catch Spotify API updates
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: playerKeys.state() });
      }, 500);
    },
  });

  const seek = useMutation({
    mutationFn: (request: SeekRequest) => playerService.seek(request),
    onSuccess: async () => {
      // Immediate refetch
      await queryClient.refetchQueries({ queryKey: playerKeys.state() });

      // Refetch again after 500ms to catch Spotify API updates
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: playerKeys.state() });
      }, 500);
    },
  });

  const setVolume = useMutation({
    mutationFn: (request: SetVolumeRequest) => playerService.setVolume(request),
    onSuccess: async () => {
      // Refetch devices to update volume display
      await queryClient.refetchQueries({ queryKey: playerKeys.devices() });
    },
  });

  return { play, pause, next, previous, seek, setVolume };
}
